/**
 * Per-category content registry for CategoryLanding.
 *
 * heroArt/heroArtDark/badge are optional — when absent the component renders
 * only the text block (no art placeholder, no icon fallback).
 * Convention for future hero images: src/assets/services/landings/<key>-hero.svg
 */
import computeBadge from '@/assets/services/compute-badge.svg';
import computeHero from '@/assets/services/compute-hero.svg';
import securityBadge from '@/assets/services/security-badge.svg';
import securityHero from '@/assets/services/security-hero.svg';
import securityHeroDark from '@/assets/services/security-hero-dark.svg';

export interface CategoryLandingContent {
  /** Breadcrumb label, e.g. 'Compute'. */
  label: string;
  /** Suffix for "Welcome to {brand.shortName} {label}" headline. */
  headline: string;
  /** 1–2 sentence marketing lede. */
  lede: string;
  /** 3–4 core feature value props. */
  coreFeatures: { title: string; desc: string }[];
  /** Optional hero art (light mode). Absent → no art block rendered. */
  heroArt?: string;
  /** Optional hero art (dark mode override). */
  heroArtDark?: string;
  /** Optional badge shown in the title row. */
  badge?: string;
}

export const LANDING_CONTENT: Record<string, CategoryLandingContent> = {
  compute: {
    label: 'Compute',
    headline: 'Compute',
    lede: `Launch and operate virtual machines on your own cloud infrastructure — provision instances, attach storage, wire up networking and track quota from a single console, with full root access to every machine.`,
    coreFeatures: [
      { title: 'Instances to your spec', desc: 'Spin up VMs with the exact vCPU, memory and OS image you need, and keep full root control over each one.' },
      { title: 'Storage', desc: 'Attach, resize and snapshot SSD volumes, and schedule automated backups to protect every instance’s data.' },
      { title: 'Networking', desc: 'Build isolated VPCs with static IPs, VPN, load balancers, security groups and managed TLS certificates.' },
      { title: 'Access & usage', desc: 'Manage SSH keys and monitor real-time performance, traffic and billable quota across your whole fleet.' },
    ],
    heroArt: computeHero,
    badge: computeBadge,
  },

  security: {
    label: 'Security',
    headline: 'Security',
    lede: 'Protect your websites, APIs and certificates from a single policy at the edge — malicious and automated traffic is inspected and filtered before it ever reaches your origin.',
    coreFeatures: [
      { title: 'AI-WAF', desc: 'An AI-assisted web application firewall blocks OWASP Top-10 and zero-day application-layer attacks in real time.' },
      { title: 'Bot management', desc: 'Tell real users apart from scrapers, credential-stuffing and other automated abuse, then challenge or block them.' },
      { title: 'API protection', desc: 'Automatically discover your API endpoints and enforce schema, rate-limit and authentication controls on each.' },
      { title: 'DDoS & emergency mitigation', desc: 'Absorb volumetric layer-3/4 and layer-7 attacks at the edge, with on-demand emergency mitigation when needed.' },
    ],
    heroArt: securityHero,
    heroArtDark: securityHeroDark,
    badge: securityBadge,
  },

  streaming: {
    label: 'Media & Streaming',
    headline: 'Media & Streaming',
    lede: 'Deliver live and on-demand video at scale — from ingest and real-time transcoding to DRM-protected, low-latency, multi-CDN distribution worldwide.',
    coreFeatures: [
      { title: 'Live streaming', desc: 'Enterprise live transcoding with low-latency HLS delivery, DRM and multi-CDN failover for large audiences.' },
      { title: 'On-demand video', desc: 'Transcode, package and adaptively stream your VOD library with subtitle and thumbnail management built in.' },
      { title: 'FAST channels', desc: 'Run free ad-supported streaming TV channels with scheduling, 24/7 playout and EPG integration.' },
      { title: 'AES key gateway', desc: 'Centralised HLS AES-128 key issuance and rotation that secures both live and VOD content.' },
    ],
  },

  cdn: {
    label: 'Networking & CDN',
    headline: 'Networking & CDN',
    lede: 'Edge delivery and security for all your domains — caching, multi-CDN orchestration, WAF and TLS, managed together from one interface.',
    coreFeatures: [
      { title: 'Multi-CDN orchestration', desc: 'Route and balance traffic across multiple CDN backends automatically for the best performance, availability and cost.' },
      { title: 'Domain management', desc: 'Centrally manage CNAME routing, cache rules, cache purging and per-domain health from one place.' },
      { title: 'WAF & bot protection', desc: 'Filter malicious requests and bots at the edge using managed OWASP rule sets and custom policies.' },
      { title: 'TLS certificates', desc: 'Issue, deploy and auto-renew TLS certificates for every domain with no manual steps.' },
    ],
  },

  kubernetes: {
    label: 'Kubernetes',
    headline: 'Kubernetes',
    lede: 'Run containerised workloads on fully managed Kubernetes — provision clusters, scale node pools, expose services and store images without ever operating the control plane yourself.',
    coreFeatures: [
      { title: 'Managed clusters', desc: 'Provision, upgrade and self-heal production clusters while we operate the control plane for you.' },
      { title: 'Node pools', desc: 'Group heterogeneous instance types into separate node pools and auto-scale each to match demand.' },
      { title: 'Ingress & networking', desc: 'Built-in load balancers, ingress controllers and network policies to expose and secure workloads.' },
      { title: 'Container registry', desc: 'Store private container images with automated vulnerability scanning and fine-grained access control.' },
    ],
  },

  databases: {
    label: 'Databases',
    headline: 'Databases',
    lede: 'Managed relational and cache databases with automated backups, monitoring and point-in-time restore — deploy production-ready data stores without needing a DBA.',
    coreFeatures: [
      { title: 'Relational databases', desc: 'Fully managed MySQL, PostgreSQL and MariaDB with high-availability replicas and automatic failover.' },
      { title: 'Redis cache', desc: 'Managed Redis with configurable persistence, eviction policies and live performance metrics.' },
      { title: 'Automated backups', desc: 'Daily snapshots plus point-in-time restore so you can recover any database to any moment.' },
      { title: 'Monitoring & alerts', desc: 'Track query throughput and slow logs, with threshold-based alerts before issues impact users.' },
    ],
  },

  storage: {
    label: 'Storage',
    headline: 'Storage',
    lede: 'S3-compatible object storage built for durability and scale — keep assets, backups and datasets accessible through the standard S3 tooling you already use.',
    coreFeatures: [
      { title: 'S3-compatible API', desc: 'A drop-in replacement for AWS S3 that works with any existing S3 SDK, CLI or integration.' },
      { title: 'Versioning', desc: 'Retain every version of an object and roll back to a previous state instantly after mistakes.' },
      { title: 'Access key management', desc: 'Issue per-application credentials and enforce fine-grained bucket and object access policies.' },
      { title: 'Public & private buckets', desc: 'Serve static assets publicly over HTTPS or lock sensitive data down to private access only.' },
    ],
  },

  observability: {
    label: 'Observability',
    headline: 'Observability',
    lede: 'Monitor uptime, latency and certificate health across your whole estate — detect and get alerted on problems before your users ever notice them.',
    coreFeatures: [
      { title: 'Uptime monitoring', desc: 'Run HTTP, TCP and ICMP checks from multiple global vantage points on a schedule you control.' },
      { title: 'Latency tracking', desc: 'Measure response times over time and surface regional degradations before they escalate.' },
      { title: 'TLS expiry alerts', desc: 'Track certificate expiry across every domain and get warned early to prevent avoidable outages.' },
      { title: 'Multi-channel alerting', desc: 'Deliver alerts to email, Slack, webhooks and on-call tools so the right people respond fast.' },
    ],
  },

  management: {
    label: 'Management',
    headline: 'Management',
    lede: 'Govern identity, teams, billing and API access across your entire cloud footprint from one management plane — with a full audit trail of every change.',
    coreFeatures: [
      { title: 'Organizations & teams', desc: 'Structure resources into organizations and projects with role-based access control per member.' },
      { title: 'API key management', desc: 'Issue, scope and revoke programmatic credentials for every service and third-party integration.' },
      { title: 'Usage & cost tracking', desc: 'See itemised invoices and real-time, per-service usage so spend never surprises you.' },
      { title: 'Audit log', desc: 'Keep an immutable, searchable record of every action taken across your organization.' },
    ],
  },
};
